import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

export type GroupingMode = 'day' | 'user'

interface ApprovalStore {
    groupingMode: GroupingMode
    setGroupingMode: (mode: GroupingMode) => void

    expandedDayKeys: Record<string, true>
    expandedUserKeys: Record<string, true>
    toggleDayKey: (key: string) => void
    toggleUserKey: (key: string) => void

    // Clears both expansion maps in one update so stale dateKey/userId entries
    // don't survive across periods.
    resetExpansion: () => void
}

function toggleKey(map: Record<string, true>, key: string): Record<string, true> {
    if (map[key]) {
        const next = { ...map }
        delete next[key]
        return next
    }
    return { ...map, [key]: true }
}

export const useApprovalStore = create<ApprovalStore>()(set => ({
    groupingMode: 'user',
    setGroupingMode: mode => set({ groupingMode: mode }),

    expandedDayKeys: {},
    expandedUserKeys: {},
    toggleDayKey: key => set(state => ({ expandedDayKeys: toggleKey(state.expandedDayKeys, key) })),
    toggleUserKey: key =>
        set(state => ({ expandedUserKeys: toggleKey(state.expandedUserKeys, key) })),

    resetExpansion: () => set({ expandedDayKeys: {}, expandedUserKeys: {} }),
}))

export const useApprovalGroupingMode = () =>
    useApprovalStore(
        useShallow(s => ({
            groupingMode: s.groupingMode,
            setGroupingMode: s.setGroupingMode,
        }))
    )

export const useApprovalDayExpansion = () =>
    useApprovalStore(
        useShallow(s => ({
            expandedDayKeys: s.expandedDayKeys,
            toggleDayKey: s.toggleDayKey,
        }))
    )

export const useApprovalUserExpansion = () =>
    useApprovalStore(
        useShallow(s => ({
            expandedUserKeys: s.expandedUserKeys,
            toggleUserKey: s.toggleUserKey,
        }))
    )

export const useApprovalResetExpansion = () => useApprovalStore(s => s.resetExpansion)
